import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Button, CircularProgress } from '@mui/material';
import { CalendarX, Plus } from 'lucide-react';
import { useSchool } from '@/hooks/useSchool';
import academicSessionService from '@/services/academicSessionService';
import { tokens } from '@/theme/tokens';
import { WidgetContainer } from '@/components/dashboard/WidgetContainer';

export function AcademicSessionGuard({ children }) {
  const { schoolId } = useSchool();
  const navigate = useNavigate();
  const [sessionsLoaded, setSessionsLoaded] = useState(false);
  const [hasActiveSession, setHasActiveSession] = useState(false);

  useEffect(() => {
    let isMounted = true;
    if (schoolId) {
      academicSessionService
        .getSessionsBySchool(schoolId)
        .then((res) => {
          if (!isMounted) return;
          const sessions = Array.isArray(res) ? res : (res?.data || []);
          const active = sessions.some((s) => s.is_current || s.status === 'ACTIVE');
          setHasActiveSession(active);
          setSessionsLoaded(true);
        })
        .catch(() => {
          if (isMounted) {
            setHasActiveSession(false);
            setSessionsLoaded(true);
          }
        });
    } else {
      setSessionsLoaded(true);
    }

    return () => {
      isMounted = false;
    };
  }, [schoolId]);

  if (!sessionsLoaded) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!hasActiveSession) {
    return (
      <Box sx={{ p: 3 }}>
        <WidgetContainer>
          <Box
            sx={{
              py: 6,
              px: 3,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
            }}
          >
            <Box
              sx={{
                width: 72,
                height: 72,
                borderRadius: '50%',
                backgroundColor: tokens.colors.primary[50],
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2.5,
              }}
            >
              <CalendarX size={36} color={tokens.colors.primary.main} />
            </Box>
            <Typography variant="h5" fontWeight={700} gutterBottom>
              No Active Academic Session Found
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 500, mb: 3.5, lineHeight: 1.6 }}>
              There is no active academic session configured for this school. Please create or activate an academic session first before proceeding.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              size="large"
              startIcon={<Plus size={20} />}
              onClick={() => navigate('/academic-sessions')}
              sx={{ borderRadius: tokens.radius.full, px: 4, py: 1.2 }}
            >
              Create Academic Session
            </Button>
          </Box>
        </WidgetContainer>
      </Box>
    );
  }

  return children;
}
